/**
 * Shared קוֹל document helpers: human labels, an icon per type, and calm
 * expiry framing (never alarming red — clay for "soon", muted for "expired").
 */
import i18n from '@/i18n';
import type { CarnafDocumentType } from '@/lib/api';
import type { IconName } from '@/components/kol/Icon';

type T = (k: string, o?: Record<string, unknown>) => string;

// Date locale per UI language. `ar-u-nu-latn` keeps Latin digits in Arabic so
// dates read consistently with the rest of the (Latin-numeral) UI.
const DATE_LOCALE: Record<string, string> = { he: 'he-IL', en: 'en-GB', ar: 'ar-u-nu-latn' };

/** Line-icon per document type (Editorial Calm — retires emoji). Accepts a
 * raw string too (Ask citations carry documentType as string). */
export function docTypeIconName(type: CarnafDocumentType | string): IconName {
  switch (type) {
    case 'INSURANCE_COMPULSORY':
    case 'INSURANCE_COMPREHENSIVE':
    case 'INSURANCE_POLICY':
      return 'shield';
    case 'SERVICE_CONTRACT':
      return 'wrench';
    case 'VEHICLE_REGISTRATION':
    case 'DRIVER_LICENSE':
    case 'OTHER':
    default:
      return 'folder';
  }
}

export function docTypeLabel(type: CarnafDocumentType, t: T): string {
  switch (type) {
    case 'VEHICLE_REGISTRATION':
      return t('doc_types.vehicle_registration', { defaultValue: 'רישיון רכב' });
    case 'DRIVER_LICENSE':
      return t('doc_types.driver_license', { defaultValue: 'רישיון נהיגה' });
    case 'INSURANCE_COMPULSORY':
      return t('doc_types.insurance_compulsory', { defaultValue: 'ביטוח חובה' });
    case 'INSURANCE_COMPREHENSIVE':
      return t('doc_types.insurance_comprehensive', { defaultValue: 'ביטוח מקיף' });
    case 'SERVICE_CONTRACT':
      return t('doc_types.service_contract', { defaultValue: 'כתב שירות' });
    case 'INSURANCE_POLICY':
      return t('doc_types.insurance_policy', { defaultValue: 'פוליסת ביטוח' });
    case 'OTHER':
      return t('doc_types.other', { defaultValue: 'מסמך אחר' });
  }
}

export function docTypeIcon(type: CarnafDocumentType): string {
  switch (type) {
    case 'VEHICLE_REGISTRATION':
      return '🚗';
    case 'DRIVER_LICENSE':
      return '🪪';
    case 'INSURANCE_COMPULSORY':
    case 'INSURANCE_COMPREHENSIVE':
    case 'INSURANCE_POLICY':
      return '🛡️';
    case 'SERVICE_CONTRACT':
      return '🔧';
    case 'OTHER':
      return '📄';
  }
}

export function formatDocDate(iso: string | null): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const lng = (i18n.language || 'he').split('-')[0] || 'he';
  return d.toLocaleDateString(DATE_LOCALE[lng] ?? 'he-IL', { day: 'numeric', month: 'long', year: 'numeric' });
}

export type ExpiryState = 'none' | 'ok' | 'soon' | 'expired';

/** Calm tri-state: warn ("soon") within 30 days, "expired" once past. */
export function expiryState(iso: string | null): ExpiryState {
  if (!iso) return 'none';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return 'none';
  const days = Math.ceil((d.getTime() - Date.now()) / (1000 * 60 * 60 * 24));
  if (days < 0) return 'expired';
  if (days <= 30) return 'soon';
  return 'ok';
}
